<section class="debug-panel" aria-label="Run debug panel">
  <header class="debug-panel__header">
    <div>
      <p class="debug-panel__eyebrow">Run debug</p>
      <h2 class="debug-panel__title">Context and replay</h2>
    </div>
    @if (run()) {
      <span class="status-pill" [ngClass]="statusClass(run()?.status)">{{ humanizeStatus(run()?.status) }}</span>
    }
  </header>

  @if (runLoading()) {
    <div class="debug-panel__loading">
      <mat-progress-spinner diameter="24" mode="indeterminate"></mat-progress-spinner>
      <span>Loading run details...</span>
    </div>
  } @else if (runError()) {
    <p class="debug-panel__message debug-panel__message--error">{{ runError() }}</p>
  } @else if (run()) {
    <div class="debug-grid">
      <section class="debug-card">
        <h3>Run summary</h3>
        <dl class="summary-grid">
          <div>
            <dt>Run</dt>
            <dd>{{ run()?.id }}</dd>
          </div>
          <div>
            <dt>Workflow</dt>
            <dd>{{ run()?.workflow_id }}</dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{{ formatTimestamp(run()?.created_at) }}</dd>
          </div>
          <div>
            <dt>Started</dt>
            <dd>{{ formatTimestamp(run()?.started_at) }}</dd>
          </div>
          <div>
            <dt>Finished</dt>
            <dd>{{ formatTimestamp(run()?.finished_at) }}</dd>
          </div>
          <div>
            <dt>Duration</dt>
            <dd>{{ runDuration() }}</dd>
          </div>
          <div>
            <dt>Attempt</dt>
            <dd>{{ run()?.attempt ?? '-' }}</dd>
          </div>
          <div>
            <dt>Retries</dt>
            <dd>{{ run()?.retries ?? '-' }}</dd>
          </div>
          <div>
            <dt>Workflow version</dt>
            <dd>{{ run()?.workflow_version ?? '-' }}</dd>
          </div>
          <div>
            <dt>Steps</dt>
            <dd>{{ (run()?.steps ?? []).length }}</dd>
          </div>
        </dl>

        @if (stepStatusSummary().length > 0) {
          <div class="pill-row">
            @for (item of stepStatusSummary(); track item.label) {
              <span class="status-pill" [ngClass]="item.className">{{ item.label }} {{ item.count }}</span>
            }
          </div>
        }
      </section>

      <section class="debug-card">
        <div class="debug-card__heading">
          <h3>Replay summary</h3>
          <span class="debug-card__caption">Summary only. Not a canonical event timeline.</span>
        </div>

        @if (replayLoading()) {
          <p class="debug-panel__message">Loading replay summary...</p>
        } @else if (replayError()) {
          <p class="debug-panel__message debug-panel__message--error">{{ replayError() }}</p>
        } @else if (replay()) {
          <dl class="summary-grid">
            <div>
              <dt>Plan version</dt>
              <dd>{{ replay()?.plan_version ?? '-' }}</dd>
            </div>
            <div>
              <dt>Nodes</dt>
              <dd>{{ replay()?.node_count ?? '-' }}</dd>
            </div>
            <div>
              <dt>Edges</dt>
              <dd>{{ replay()?.edge_count ?? '-' }}</dd>
            </div>
            <div>
              <dt>Events</dt>
              <dd>{{ replay()?.event_count ?? '-' }}</dd>
            </div>
            <div>
              <dt>Last event</dt>
              <dd>{{ replay()?.last_event_type || '-' }}</dd>
            </div>
          </dl>

          @if (replayEventCounts().length > 0) {
            <div class="event-counts">
              @for (entry of replayEventCounts(); track entry[0]) {
                <div class="event-counts__item">
                  <span>{{ entry[0] }}</span>
                  <strong>{{ entry[1] }}</strong>
                </div>
              }
            </div>
          } @else {
            <p class="debug-panel__message">No replay events persisted for this run.</p>
          }
        } @else {
          <p class="debug-panel__message">Replay summary unavailable.</p>
        }
      </section>

      <section class="debug-card">
        <h3>Runtime context</h3>
        @if (contextLoading()) {
          <p class="debug-panel__message">Loading runtime context...</p>
        } @else if (contextError()) {
          <p class="debug-panel__message debug-panel__message--error">{{ contextError() }}</p>
        } @else if (hasContext()) {
          <pre class="json-block">{{ formatJson(context()) }}</pre>
        } @else {
          <p class="debug-panel__message">No runtime context persisted for this run.</p>
        }
      </section>
    </div>

    <section class="debug-card">
      <div class="debug-card__heading">
        <h3>Step timeline</h3>
        <span class="debug-card__caption">Persisted step status and duration.</span>
      </div>

      @if ((run()?.steps ?? []).length === 0) {
        <p class="debug-panel__message">No steps recorded for this run.</p>
      } @else {
        <ol class="timeline">
          @for (step of run()?.steps ?? []; track step.id) {
            <li class="timeline__item">
              <div class="timeline__row">
                <strong>{{ step.node_id }}</strong>
                <span class="status-pill" [ngClass]="statusClass(step.status)">{{ humanizeStatus(step.status) }}</span>
              </div>
              <div class="timeline__meta">
                <span>{{ step.type }}</span>
                <span>Attempt {{ step.attempt }}</span>
                <span>Retries {{ step.retries ?? 0 }}</span>
                <span>{{ formatDuration(step.duration_ms) }}</span>
              </div>
              @if (step.started_at || step.finished_at) {
                <div class="timeline__meta">
                  <span>Started {{ formatTimestamp(step.started_at) }}</span>
                  <span>Finished {{ formatTimestamp(step.finished_at) }}</span>
                </div>
              }
              @if (step.error?.message) {
                <p class="timeline__error">{{ step.error?.message }}</p>
              }
            </li>
          }
        </ol>
      }
    </section>
  } @else {
    <p class="debug-panel__message">Run debug data is not available yet.</p>
  }
</section>
